import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, ErroreApi } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Attesa, Avviso, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import { Bottone, Etichetta, Paginazione, Riquadro, Tabella } from '../componenti/base.tsx'
import { giornoLeggibile } from './SchedaSezioni.tsx'
import { STATI_SCHEDA, datoInFrase, etichettaStatoScheda, titoloDato } from '../servizi/dati-mancanti.ts'

/**
 * Schede da completare (460, segnalazione di CIDIS del 19/10/2026): le schede dell'ambito di chi
 * guarda raggruppate per dato mancante, con i conteggi, e da ciascuna il collegamento diretto a
 * «Completa la scheda». In fondo le firme di terzi da completare, che la Direzione riceve negli
 * avvisi. Conti ed elenchi li fa l'API nel database, con la stessa definizione della scheda.
 */

type Gruppo = { codice: string; obbligatorio: boolean; schede: number }
type Riepilogo = {
  da_completare: number; dal_vecchio_archivio: number; da_verificare: number; vede_tutta_cidis: boolean
  gruppi: Gruppo[]; firme_terzi: { aperte: number; scadute: number } | null
}
type RigaScheda = {
  id: string; numero_scheda: number; etichetta: string; mascherata: boolean; stato: string
  vecchio_archivio: boolean; da_verificare: boolean; sede: { id: string; nome: string | null } | null
  /** Scheda con dati anonimizzati (481). */
  dati_anonimizzati?: boolean
  obbligatori: string[]; consigliati: string[]
}
type RigaFirma = {
  firma: string; persona_id: string; numero_scheda: number; etichetta: string; mascherata: boolean
  sede_nome: string | null; firmatario: string; revoca: boolean; registrata_il: string
  completare_entro: string; termine_scaduto: boolean
}
type Elenco<T> = { totale: number; risultati: T[] }

const LIMITE = 25

/** Chi ha firmato, nell'elenco: le stesse parole degli avvisi (servizi/firme-terzi.ts). */
const FIRMATARI: Record<string, string> = {
  genitore: 'Un genitore',
  tutore: 'Il tutore',
  amministratore_sostegno: 'L’amministratore di sostegno',
  responsabile_struttura: 'Il responsabile della struttura di accoglienza',
}

const maiuscola = (testo: string) => testo.charAt(0).toUpperCase() + testo.slice(1)

const messaggioDi = (e: unknown) => (e instanceof ErroreApi
  ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

export function DaCompletare() {
  const { puo } = useSessione()
  const [parametri, setParametri] = useSearchParams()
  const dato = parametri.get('dato') ?? ''
  const vista = parametri.get('vista') === 'firme' ? 'firme' : 'schede'
  const [riepilogo, setRiepilogo] = useState<Riepilogo | null>(null)
  const [errore, setErrore] = useState<string | null>(null)

  useEffect(() => {
    api.get<Riepilogo>('/persone/da-completare').then(setRiepilogo).catch((e) => setErrore(messaggioDi(e)))
  }, [])

  const scegli = (valori: { dato?: string; vista?: string }) => {
    const nuovi = new URLSearchParams()
    if (valori.dato) nuovi.set('dato', valori.dato)
    if (valori.vista) nuovi.set('vista', valori.vista)
    setParametri(nuovi, { replace: true })
  }

  if (errore) return <Avviso tipo="errore">{errore}</Avviso>
  if (!riepilogo) return <Attesa testo="Conteggio delle schede…" />

  const obbligatori = riepilogo.gruppi.filter((g) => g.obbligatorio)
  const consigliati = riepilogo.gruppi.filter((g) => !g.obbligatorio)
  const firme = riepilogo.firme_terzi

  return (
    <>
      <Intestazione
        titolo="Da completare"
        sottotitolo={riepilogo.da_completare === 0
          ? 'Nessuna scheda a cui mancano dati obbligatori, fra quelle che puoi vedere.'
          : `${riepilogo.da_completare === 1 ? '1 scheda' : `${riepilogo.da_completare} schede`} a cui mancano dati obbligatori`
            + (riepilogo.dal_vecchio_archivio ? `, di cui ${riepilogo.dal_vecchio_archivio} dal vecchio archivio` : '')
            + ', fra quelle che puoi vedere.'}
      />

      {!riepilogo.vede_tutta_cidis && (
        <Avviso tipo="informazione">
          Le schede senza Sede Servizio le vede per intero solo chi ha un incarico su tutta CIDIS: qui
          compaiono solo quelle che rientrano nei tuoi incarichi per area, progetto o tipologia dei percorsi.
        </Avviso>
      )}

      <Riquadro titolo="Che cosa manca">
        <p className="nota-modulo">
          Gli obbligatori decidono lo stato: finché ne manca uno la scheda resta «da completare». I consigliati si
          possono saltare. Scegli un dato per vedere le schede a cui manca.
        </p>
        <dl className="elenco-dati">
          <VoceGruppo attivo={vista === 'schede' && !dato} nome="Tutte le schede da completare"
                      numero={riepilogo.da_completare} scegli={() => scegli({})} />
        </dl>
        {obbligatori.length > 0 && (
          <>
            <p className="persona-sottotitolo">Obbligatori</p>
            <dl className="elenco-dati">
              {obbligatori.map((g) => (
                <VoceGruppo key={g.codice} attivo={vista === 'schede' && dato === g.codice} nome={titoloDato(g.codice)}
                            numero={g.schede} scegli={() => scegli({ dato: g.codice })} />
              ))}
            </dl>
          </>
        )}
        {consigliati.length > 0 && (
          <>
            <p className="persona-sottotitolo">Consigliati, si possono saltare</p>
            <dl className="elenco-dati">
              {consigliati.map((g) => (
                <VoceGruppo key={g.codice} attivo={vista === 'schede' && dato === g.codice} nome={titoloDato(g.codice)}
                            numero={g.schede} scegli={() => scegli({ dato: g.codice })} />
              ))}
            </dl>
          </>
        )}
        <p className="persona-sottotitolo">Altro da sistemare</p>
        <dl className="elenco-dati">
          {firme && (
            <VoceGruppo attivo={vista === 'firme'} nome="Firme di terzi da completare" numero={firme.aperte}
                        nota={firme.scadute ? `${firme.scadute} con il termine scaduto` : undefined}
                        scegli={() => scegli({ vista: 'firme' })} />
          )}
          <div className="dato">
            <dt><Link to="/persone?da_verificare=true">Schede da verificare</Link></dt>
            <dd className="mono">{riepilogo.da_verificare}</dd>
          </div>
        </dl>
      </Riquadro>

      {vista === 'firme' && firme
        ? <ElencoFirme />
        : <ElencoSchede dato={dato} completa={puo('persona.modifica')} />}
    </>
  )
}

function VoceGruppo({ nome, numero, attivo, scegli, nota }: {
  nome: string; numero: number; attivo: boolean; scegli: () => void; nota?: string
}) {
  return (
    <div className="dato">
      <dt className="dato-con-etichetta">
        {attivo
          ? <strong aria-current="true">{nome}</strong>
          : <Bottone variante="discreto" dimensione="piccolo" onClick={scegli}>{nome}</Bottone>}
        {nota && <Etichetta tono="attenzione">{nota}</Etichetta>}
      </dt>
      <dd className="mono">{numero}</dd>
    </div>
  )
}

function ElencoSchede({ dato, completa }: { dato: string; completa: boolean }) {
  const [salta, setSalta] = useState(0)
  const [esito, setEsito] = useState<Elenco<RigaScheda> | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [chiave, setChiave] = useState(dato)
  if (chiave !== dato) { setChiave(dato); setSalta(0) }

  useEffect(() => {
    let vivo = true
    setEsito(null)
    const q = new URLSearchParams({ limite: String(LIMITE), salta: String(salta) })
    if (dato) q.set('dato', dato)
    api.get<Elenco<RigaScheda>>(`/persone/da-completare/schede?${q}`)
      .then((r) => { if (vivo) { setEsito(r); setErrore(null) } })
      .catch((e) => { if (vivo) setErrore(messaggioDi(e)) })
    return () => { vivo = false }
  }, [dato, salta])

  return (
    <Riquadro denso titolo={dato ? `A cui manca ${datoInFrase(dato)}` : 'Schede da completare'}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!esito && !errore && <Attesa />}
      {esito && (
        <>
          <div className="persona-da-completare">
          <Tabella intestazioni={['Scheda', 'Persona', 'Sede', 'Che cosa manca', '']}
                   vuoto={<Vuoto testo="Nessuna scheda, fra quelle che puoi vedere." />}>
            {esito.risultati.map((r) => (
              <tr key={r.id}>
                <td data-etichetta="Scheda" className="mono">{r.numero_scheda}</td>
                {/* Un solo elemento per cella: sullo schermo stretto la cella e' una riga flessibile. */}
                <td data-etichetta="Persona">
                  <span>
                    <Link to={`/persone/${r.id}`} className="voce-principale">{r.etichetta}</Link>
                    {' '}
                    <span title={STATI_SCHEDA[r.stato]?.aiuto}>
                      <Etichetta tono={r.stato === 'completa' ? 'positivo' : 'attenzione'}>{etichettaStatoScheda(r.stato)}</Etichetta>
                    </span>
                    {r.da_verificare && <> <Etichetta tono="attenzione">da verificare</Etichetta></>}
                    {r.dati_anonimizzati && <> <Etichetta tono="informativo">Dati anonimizzati</Etichetta></>}
                  </span>
                </td>
                <td data-etichetta="Sede">{r.sede?.nome ?? <span className="tenue">senza sede</span>}</td>
                <td data-etichetta="Che cosa manca">
                  <span>
                    {maiuscola(r.obbligatori.map(datoInFrase).join(', '))}
                    {r.consigliati.length > 0 && (
                      <span className="piccolo tenue">
                        {r.obbligatori.length ? ' · ' : ''}
                        {r.consigliati.length === 1 ? '1 consigliato' : `${r.consigliati.length} consigliati`}
                      </span>
                    )}
                  </span>
                </td>
                <td data-etichetta="">
                  {completa && !r.mascherata
                    ? <Link className="bottone bottone-secondario bottone-piccolo" to={`/persone/${r.id}?completa=1`}>Completa la scheda</Link>
                    : <Link to={`/persone/${r.id}`}>Apri la scheda</Link>}
                </td>
              </tr>
            ))}
          </Tabella>
          </div>
          {esito.risultati.some((r) => r.mascherata) && (
            <p className="nota-elenco">
              Le righe con le iniziali sono schede che vedi mascherate: i dati li completa chi le segue.
            </p>
          )}
          <Paginazione salta={salta} limite={LIMITE} totale={esito.totale} ricevute={esito.risultati.length} cambia={setSalta} />
        </>
      )}
    </Riquadro>
  )
}

function ElencoFirme() {
  const [salta, setSalta] = useState(0)
  const [esito, setEsito] = useState<Elenco<RigaFirma> | null>(null)
  const [errore, setErrore] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    setEsito(null)
    api.get<Elenco<RigaFirma>>(`/persone/da-completare/firme?limite=${LIMITE}&salta=${salta}`)
      .then((r) => { if (vivo) { setEsito(r); setErrore(null) } })
      .catch((e) => { if (vivo) setErrore(messaggioDi(e)) })
    return () => { vivo = false }
  }, [salta])

  return (
    <Riquadro denso titolo="Firme di terzi da completare">
      <p className="nota-modulo">
        Ha firmato un’altra persona al posto dell’interessato e manca il documento che ne prova il potere di
        firma. I consensi valgono e gli interventi non si bloccano; il documento si carica dalla sezione
        Consensi della scheda, con il percorso guidato.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!esito && !errore && <Attesa />}
      {esito && (
        <>
          <div className="persona-da-completare">
          <Tabella intestazioni={['Scheda', 'Persona', 'Firmato da', 'Registrata il', 'Termine']}
                   vuoto={<Vuoto testo="Nessuna firma da completare, fra le schede che puoi vedere." />}>
            {esito.risultati.map((r) => (
              <tr key={r.firma}>
                <td data-etichetta="Scheda" className="mono">{r.numero_scheda}</td>
                <td data-etichetta="Persona">
                  <span>
                    <Link to={`/persone/${r.persona_id}`} className="voce-principale">{r.etichetta}</Link>
                    {r.sede_nome && <span className="piccolo tenue"> · {r.sede_nome}</span>}
                  </span>
                </td>
                <td data-etichetta="Firmato da">
                  <span>{FIRMATARI[r.firmatario] ?? 'Un’altra persona'}{r.revoca ? ' (revoca)' : ''}</span>
                </td>
                <td data-etichetta="Registrata il">{giornoLeggibile(r.registrata_il)}</td>
                <td data-etichetta="Termine">
                  {r.termine_scaduto
                    ? <Etichetta tono="critico">scaduto il {giornoLeggibile(r.completare_entro)}</Etichetta>
                    : <span>entro il {giornoLeggibile(r.completare_entro)}</span>}
                </td>
              </tr>
            ))}
          </Tabella>
          </div>
          <Paginazione salta={salta} limite={LIMITE} totale={esito.totale} ricevute={esito.risultati.length} cambia={setSalta} />
        </>
      )}
    </Riquadro>
  )
}
